<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0
-->

<script>
import { afterUpdate, onDestroy, onMount, tick } from 'svelte';
import { fly } from 'svelte/transition';
import Alert from '../../components/Alert.svelte';
import FeedbackButtons from '../../components/FeedbackButtons.svelte';
import InputTextarea from '../../components/InputTextarea.svelte';
import LoaderAnimation from '../../components/LoaderAnimation.svelte';
import ModelSelection from '../../components/ModelSelection.svelte';
import { getApiUrl, getChatConfig } from '../../config.js';
import { sendChatMessage } from '../../utils/ChatService.js';
import sendFeedback from '../../utils/SendFeedback.js';
import uiStore from '../../utils/UIStore';
import { copyWithDisclaimers } from '../../utils/copyHook.js';
import documentStore from '../../utils/documentStore';
import { renderMarkdown } from '../../utils/markdownParser';
import { estimateMessageListTokens, estimateTokens } from '../../utils/tokenEstimate.js';
import websocketsStore from '../../utils/websocketsStore';

import ExamplePrompts from '../../components/ExamplePrompts.svelte';
import DocumentBar from './DocumentBar.svelte';

const CHAT_CHAR_LIMIT = getChatConfig().char_limit;
const CHAT_CONTEXT_SAFE_TOKENS = getChatConfig().context_safe_tokens;
const SYSTEM_PROMPT_TOKEN_OVERHEAD = 500;
const CHAT_COPY_DISCLAIMER =
  'Hinweis: Dieser Text wurde mit KI-Unterstützung erzeugt und kann Fehler enthalten. Bitte prüfen Sie den Inhalt vor der weiteren Verwendung.';

// component state variables
let messages = [];
let newMessage = '';
let lastUserMessage = '';
let llm = {};
let llm_selected_by_user = false;
let available_llms = [];
let sendButtonTooltip = '';
let chatText = '';
let warningCloud = '';
let warningWebsearch = '';
let warningCloudShort = '';
let warningCloudWebsearch = '';
let warningChat = '';
let resetTextArea;
let examplePrompts = [];
let documents = [];
let currentReasonText = '';
let showReasoning = false;
let isReasoningStreaming = false;
let isStreamingLocked = false;
let useWebsearch = false;

// Warn user if the backend takes unusually long before the first streamed token
const SLOW_RESPONSE_THRESHOLD_MS = 10000;
let showSlowResponseHint = false;
let slowResponseTimerId;
let slowResponseToastShown = false;

const streamingStatus = {
  Ready: 'ready',
  Loading: 'loading',
  Streaming: 'streaming',
  Error: 'error',
};
let currentStreamingStatus = streamingStatus.Ready;

// svelte component lifecycle for enough space when document bar is visible
// default why to react to dynmaic size changes
let resizeObserver;

onMount(() => {
  updateSendButtonTooltip();

  if (chatInputContainer && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Add a larger safety margin so last messages are never hidden behind document bar
        chatInputPaddingBottom = entry.contentRect.height + 80;
      }
    });
    resizeObserver.observe(chatInputContainer);
  }
});

//load current state from uistore
uiStore.chatStore.subscribe((store) => {
  llm = store.llm;
  llm_selected_by_user = store.llm_selected_by_user;
  newMessage = store.newMessage;
  lastUserMessage = store.lastUserMessage;

  messages = store.messages;
  if (messages.length > 0 && messages[messages.length - 1].role === 'user') {
    rollBackOnError();
  }
});

//load available llms from uistore and if available set local llm to current llm
uiStore.active_llms_chat.subscribe((active_llms) => {
  available_llms = active_llms.chat;
  const local_model = active_llms.chat.find((item) => item.name === 'local');
  if (!llm_selected_by_user) {
    if (local_model) {
      llm = local_model;
    } else {
      llm = available_llms[0];
    }
  }
});

//load ui texts from ui Store
uiStore.textStore.subscribe((store) => {
  if (store.texts != null) {
    const data = store.texts;
    chatText = renderMarkdown(data.chatText);
    warningCloud = data.warnhinweisCloud;
    warningWebsearch = data.warnhinweisWebsearch ?? '';
    warningCloudShort = data.warnhinweisCloudShort ?? data.warnhinweisCloud;
    const combined = data.warnhinweisCloudWebsearch;
    warningCloudWebsearch =
      typeof combined === 'string' && combined.trim() !== ''
        ? combined.trim()
        : `${warningWebsearch} ${warningCloudShort}`.replace(/\s+/g, ' ').trim();
    warningChat = renderMarkdown(data.warnhinweisChat);
  }
});

let features;
uiStore.featureStore.subscribe((fs) => {
  features = fs;
});

let uiTexts;
uiStore.textStore.subscribe((store) => {
  uiTexts = store.texts;
});

$: examplePrompts = uiTexts ? uiTexts.examplePromptsChat : [];

$: hasChatModel = !!(llm?.name && available_llms.length > 0);
$: websearchOn = !!(features?.tools?.onlineSearch && useWebsearch);
$: remoteModel = hasChatModel && llm.is_remote;
$: chatPrivacyAlertKind =
  remoteModel && websearchOn ? 'combined' : remoteModel ? 'remote' : websearchOn ? 'websearch' : null;
$: chatInputPrivacyTone = remoteModel ? 'remote' : websearchOn ? 'websearch' : 'none';
$: chatPrivacyAlertTone =
  chatPrivacyAlertKind === 'websearch' ? 'websearch' : chatPrivacyAlertKind === 'combined' ? 'combined' : 'cloud';
$: chatPrivacyBody =
  chatPrivacyAlertKind === 'combined'
    ? warningCloudWebsearch
    : chatPrivacyAlertKind === 'remote'
      ? warningCloudShort
      : chatPrivacyAlertKind === 'websearch'
        ? warningWebsearch
        : '';

//update uistore before end of component
onDestroy(() => {
  uiStore.chatStore.update(() => ({
    messages,
    newMessage,
    lastUserMessage,
    llm,
    llm_selected_by_user,
  }));

  if (resizeObserver && chatInputContainer) {
    resizeObserver.unobserve(chatInputContainer);
  }
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

// Subscribe to changes in document store and update local documents-list
documentStore.documents.subscribe((docs) => {
  documents = Object.values(docs);
});

let lastUpdatedMsg = null;
let chatWindow;

// Dynamically track the height of the fixed input + document bar
let chatInputContainer;
let chatInputPaddingBottom = 200;

$: isInitialState = messages.length === 0 && documents.length === 0;

function scrollElementIntoViewById(id) {
  if (typeof window === 'undefined') {
    return;
  }
  const element = document.getElementById(id);
  if (!element) {
    return;
  }

  const elementRect = element.getBoundingClientRect();
  const bottomBarHeight = chatInputContainer ? chatInputContainer.getBoundingClientRect().height : 0;

  const viewportHeight = window.innerHeight;
  const marginTop = 24;
  const marginBottom = 16;
  const availableHeight = viewportHeight - bottomBarHeight - marginTop - marginBottom;

  let targetScrollTop;

  if (elementRect.height <= availableHeight) {
    // For short messages: center the whole element in the free area above the bottom bar
    const freeAreaTop = marginTop;
    const freeAreaHeight = viewportHeight - bottomBarHeight - marginTop;
    const targetTopInViewport = freeAreaTop + (freeAreaHeight - elementRect.height) / 2;
    const delta = elementRect.top - targetTopInViewport;
    targetScrollTop = Math.max(0, window.scrollY + delta);
  } else {
    // For taller messages: align the bottom just above the bottom bar
    const targetBottom = viewportHeight - bottomBarHeight - marginBottom;
    const delta = elementRect.bottom - targetBottom;
    targetScrollTop = Math.max(0, window.scrollY + delta);
  }

  window.scrollTo({
    top: targetScrollTop,
    behavior: 'smooth',
  });
}

function handleEnterKey(event) {
  // send message on [Enter]
  if (event.key === 'Enter' && !event.shiftKey) {
    sendMessage();
    event.preventDefault();
  }

  // newline on [SHIFT+Enter]
  if (event.key === 'Enter' && event.shiftKey) {
    newMessage = `${newMessage}\n`;
    event.preventDefault();
  }
}

function injectDocumentsInChatMessage(message) {
  const parsedDocuments = documents.filter((doc) => doc.is_active && doc.status === 'parsed');

  if (parsedDocuments.length === 0) {
    return {
      ...message,
      _originalContent: message.content,
      context: [], // add file names as context indicator
    };
  }

  // build prompt with file content as context
  let prompt =
    'Berücksichtige bei Deiner Antwort die folgenden Dokumente. ' +
    'Referenziere wenn möglich bei deiner Antwort die entsprechenden Seitenzahlen, wenn sie bekannt sind.\n\n';
  const context = parsedDocuments.map(
    (doc) => `##### Datei: '${doc.file.name}' #####\n\n${doc.text}\n\n##### Ende Datei: '${doc.file.name}'#####\n\n`,
  );
  prompt += context;
  prompt += `##### User Message:\n\n" + ${message.content}`;

  return {
    ...message,
    // keep original message content
    _originalContent: message.content,
    // add modified prompt with file content
    content: prompt,
    // add file names as context indicator
    context: parsedDocuments.map((doc) => doc.file.name),
  };
}

function injectToolsInChatMessage(message) {
  if (useWebsearch) {
    return {
      ...message,
      tools: [
        {
          type: 'websearch',
          payload: {}, // payload is empty for now but can be extended in the future
        },
      ],
    };
  }

  return {
    ...message,
    tools: [], // tools are empty for now but can be extended in the future
  };
}

function clearSlowResponseState() {
  // clear slow-response state and hide the info toast
  if (slowResponseTimerId) {
    clearTimeout(slowResponseTimerId);
    slowResponseTimerId = null;
  }
  showSlowResponseHint = false;
  if (slowResponseToastShown) {
    websocketsStore.currentMessage.info.set({
      message: '',
      type: 'info',
    });
    slowResponseToastShown = false;
  }
}

async function sendMessage() {
  websocketsStore.websocket.resetMessages();
  if (newMessage.length > 0) {
    let _newMessageObj = {
      content: newMessage,
      role: 'user',
    };

    _newMessageObj = injectDocumentsInChatMessage(_newMessageObj);
    _newMessageObj = injectToolsInChatMessage(_newMessageObj);
    const newMessageObjWithoutFileContent = {
      ..._newMessageObj,
      content: _newMessageObj._originalContent,
    };

    const chat_history = [...messages];
    const estimatedPromptTokens =
      SYSTEM_PROMPT_TOKEN_OVERHEAD + estimateTokens(_newMessageObj.content) + estimateMessageListTokens(chat_history);

    if (estimatedPromptTokens > CHAT_CONTEXT_SAFE_TOKENS) {
      websocketsStore.currentMessage.error.set({
        type: 'error',
        message: `Kontextlimit überschritten: Die Anfrage enthält geschätzt ${estimatedPromptTokens} Tokens, erlaubt sind maximal ${CHAT_CONTEXT_SAFE_TOKENS}. Bitte kürzen Sie Ihre Nachricht oder deaktivieren bzw. kürzen Sie angehängte Dokumente.`,
      });
      return;
    }

    messages = [...messages, newMessageObjWithoutFileContent];
    lastUserMessage = newMessage;
    newMessage = '';
    resetTextAreaHeight();
    currentReasonText = '';
    showReasoning = false;
    isReasoningStreaming = false;

    currentStreamingStatus = streamingStatus.Loading;

    // Improved scrolling to the user's message:
    // wait for DOM update, then scroll with bottom-bar awareness
    await tick();
    scrollElementIntoViewById(getMessageIdx(messages.length - 1));

    // Reset slow response state for this request
    clearSlowResponseState();

    // If the backend takes longer than 10 seconds to send the first chunk,
    // show an inline hint and trigger a toast to reassure the user.
    slowResponseTimerId = setTimeout(() => {
      // Only show the hint if still waiting for the first chunk
      if (currentStreamingStatus === streamingStatus.Loading) {
        showSlowResponseHint = true;

        if (!slowResponseToastShown) {
          slowResponseToastShown = true;
          websocketsStore.currentMessage.info.set({
            message:
              'Die Antwort dauert aufgrund hoher Auslastung etwas länger als üblich. Bitte haben Sie ein paar Minuten Geduld.',
            type: 'info',
          });
        }
      }
    }, SLOW_RESPONSE_THRESHOLD_MS);

    let responseText = '';
    let reasonText = '';

    // Add assistant message placeholder
    messages = [
      ...messages,
      {
        role: 'assistant',
        content: responseText,
        rating: 0,
        context: _newMessageObj.context,
      },
    ];

    currentStreamingStatus = streamingStatus.Streaming;

    await sendChatMessage(_newMessageObj, chat_history, llm.name, {
      onReasonChunk: (content) => {
        // Scroll to the AI's response when actual streaming starts
        setTimeout(() => {
          scrollElementIntoViewById(getMessageIdx(messages.length - 1));
        }, 100);
        clearSlowResponseState();
        reasonText += content;
        currentReasonText = reasonText;
      },
      onResponseChunk: (content) => {
        // Scroll to the AI's response when actual streaming starts
        setTimeout(() => {
          scrollElementIntoViewById(getMessageIdx(messages.length - 1));
        }, 100);
        clearSlowResponseState();
        responseText += content;
        messages[messages.length - 1].content = responseText;
      },
      onError: (error) => {
        websocketsStore.currentMessage.error.update(() => error);
        currentStreamingStatus = streamingStatus.Error;
        clearSlowResponseState();
        rollBackOnError();
      },
      onComplete: () => {
        if (messages[messages.length - 1].content === '') {
          websocketsStore.currentMessage.error.update(() => ({
            type: 'ERROR',
            message: uiTexts.chat.noAnswerGenerated,
          }));
        }
        currentStreamingStatus = streamingStatus.Ready;
      },
    });
  }
}

function rollBackOnError() {
  currentStreamingStatus = streamingStatus.Ready;
  if (messages.length === 0) {
    return;
  }

  if (messages[messages.length - 1].role === 'user') {
    messages = messages.slice(0, messages.length - 1);
    newMessage = lastUserMessage;
  }
}

function resetUI() {
  messages = [];
  newMessage = '';
  lastUserMessage = '';
  resetTextAreaHeight();
  websocketsStore.websocket.resetMessages();
  documentStore.resetDocuments();
}

async function giveFeedback(selected_message, rating, feedback_text) {
  selected_message.rating = rating;

  const body = {
    feedback: rating,
    feedback_text: feedback_text,
    chat_answer: selected_message.content,
    user_prompt: messages[messages.length - 2].content,
    llm: {
      name: llm.name,
      label: llm.label,
      is_remote: llm.is_remote,
    },
  };

  await sendFeedback(body, `${getApiUrl()}/feedback/chat`);
}

function getMessageIdx(idx) {
  return `message_${idx}`;
}

$: if (newMessage != null) {
  updateSendButtonTooltip();
}

$: if (currentStreamingStatus === streamingStatus.Streaming || currentStreamingStatus === streamingStatus.Ready) {
  updateSendButtonTooltip();
}

$: isStreamingLocked =
  currentStreamingStatus === streamingStatus.Streaming || currentStreamingStatus === streamingStatus.Loading;

function updateSendButtonTooltip() {
  if (currentStreamingStatus === streamingStatus.Streaming) {
    sendButtonTooltip = uiTexts.chat.tooltipWait;
  } else if (newMessage === '') {
    sendButtonTooltip = uiTexts.chat.tooltipEnterRequest;
  } else {
    sendButtonTooltip = uiTexts.chat.tooltipSend;
  }
}

function resetTextAreaHeight() {
  resetTextArea();
}

function useExamplePrompt(prompt) {
  newMessage = prompt.samplePrompt;
}
</script>

<svelte:head>
  <title>{uiTexts.pageTitles.chat}</title>
</svelte:head>

<main
        class="chat-main"
        in:fly={{ y: -20, duration: 500, delay: 200 }}
>
    <div
        class="chat-page-container"
        class:initial-state={isInitialState}
        style={`padding-bottom: ${chatInputPaddingBottom}px`}
    >
        <div class="chat-header"></div>

        {#if isInitialState}
            <div class="chat-intro-wrapper">
                <div class="chat-intro-content">
                    <div class="textlink">
                        {@html chatText}
                    </div>
                </div>
            </div>
        {/if}

        <div
          class="chat-content-wrapper"
          bind:this={chatWindow}
          use:copyWithDisclaimers={{
            before: CHAT_COPY_DISCLAIMER,
            after: CHAT_COPY_DISCLAIMER,
            selectors: ['.chat-assistant-copy-text'],
          }}
        >
        {#each messages as message, idx}
          {#if !(message.role === "assistant" && idx === messages.length - 1 && currentStreamingStatus === streamingStatus.Loading)}
            <div class="chat-item" id={getMessageIdx(idx)}>
              {#if message.role === "assistant" && idx === messages.length - 1 && currentReasonText}
                <div class="chat-icon-assistant"></div>
                <div class="chat-content" style="display: flex; flex-direction: column; gap: 0.5rem;">
                  <div>
                    <div
                      class="chat-role"
                      style="display: flex; align-items: center; cursor: pointer;"
                      on:click={() => showReasoning = !showReasoning}
                      on:keydown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          showReasoning = !showReasoning;
                          e.preventDefault();
                        }
                      }}
                      tabindex="0"
                      role="button"
                      aria-pressed={showReasoning}
                    >
                      Denk-Prozess
                      <!-- Loading animation when reasoning is streaming -->
                      {#if isReasoningStreaming}
                        <span class="inline-loader" aria-hidden="true">
                          <LoaderAnimation />
                        </span>
                      {/if}
                      <span style="margin-left: 6px; font-size: 1.1em; user-select: none;">
                        {showReasoning ? '▴' : '▾'}
                      </span>
                    </div>
                    {#if showReasoning}
                      <div class="chat-text reasoning-pipe reasoning-md">
                        {@html renderMarkdown(currentReasonText, { openLinksInNewTab: true })}
                      </div>
                    {/if}
                  </div>
                  <div>
                    <div class="chat-role">{uiTexts.chat.roleAssistant}</div>
                    <div class="chat-text chat-assistant-copy-text">{@html renderMarkdown(message.content)}</div>
                    <div class="feedback-button-container">
                      {#if idx !== messages.length - 1 || currentStreamingStatus === streamingStatus.Ready && features.feedback}
                        <FeedbackButtons
                          messageContent={message.content}
                          buttonClicked={message.rating}
                          copyDisclaimerBefore={CHAT_COPY_DISCLAIMER}
                          copyDisclaimerAfter={CHAT_COPY_DISCLAIMER}
                          documents={message.context || []}
                          callback={(rating, feedback_text) =>
                            giveFeedback(message, rating, feedback_text)}
                        />
                      {/if}
                    </div>
                  </div>
                </div>
              {:else}
                {#if message.role === "assistant"}
                  <div class="chat-icon-assistant"></div>
                {/if}
                {#if message.role === "user"}
                  <div class="chat-icon-user"></div>
                {/if}
                <div class="chat-content">
                  <div class="chat-role">
                    {#if message.role === "assistant"}
                      {uiTexts.chat.roleAssistant}
                    {/if}
                    {#if message.role === "user"}
                      {uiTexts.chat.roleUser}
                    {/if}
                  </div>
                  <div class="chat-text" class:chat-assistant-copy-text={message.role === "assistant"}>
                    {@html renderMarkdown(message.content)}
                  </div>

                  {#if message.role === "assistant"}
                    <div class="feedback-button-container">
                      {#if idx !== messages.length - 1 || currentStreamingStatus === streamingStatus.Ready && features.feedback}
                        <FeedbackButtons
                          messageContent={message.content}
                          buttonClicked={message.rating}
                          copyDisclaimerBefore={CHAT_COPY_DISCLAIMER}
                          copyDisclaimerAfter={CHAT_COPY_DISCLAIMER}
                          documents={message.context || []}
                          callback={(rating, feedback_text) =>
                            giveFeedback(message, rating, feedback_text)}
                        />
                      {/if}
                    </div>
                  {/if}
                </div>
              {/if}
            </div>
          {/if}
        {/each}

                {#if currentStreamingStatus === streamingStatus.Error}
                    <div class="chat-item" id="error-icon">
                        <div class="chat-icon-assistant"></div>
                        <div class="chat-content">
                            <div class="chat-role">{uiTexts.chat.systemWarning}</div>
                            <div class="chat-text">
                                {uiTexts.chat.errorMessage}
                            </div>
                        </div>
                    </div>
                {/if}

                {#if currentStreamingStatus === streamingStatus.Loading}
                    <div class="chat-item" id="loading-icon">
                        <div class="chat-icon-assistant"></div>
                        <div class="chat-content">
                            <div class="chat-role">{uiTexts.chat.roleAssistant}</div>
                            <div class="chat-text" style="margin-top:1rem;">
                                <LoaderAnimation/>
                            </div>
                            {#if showSlowResponseHint}
                                <div class="chat-text slow-response-hint">
                                    {uiTexts.chat.slowResponseWarning}
                                </div>
                            {/if}
                        </div>
                    </div>
                {/if}
      </div>

      {#if (messages.length > 0 || documents.length > 0) && currentStreamingStatus !== streamingStatus.Loading && currentStreamingStatus !== streamingStatus.Streaming}
          <div class="reset-button-fixed">
              <button
                      class="secondary delete"
                      on:click={resetUI}
              >
                  {uiTexts.chat.resetButton}
              </button>
          </div>
      {/if}

      <div
              class="chat-input-container"
              class:initial-state-input={isInitialState}
              bind:this={chatInputContainer}
      >
          <div class="chat-input-wrapper">
              {#if isInitialState}
                  <div class="chat-welcome-headline">
                      {uiTexts.chat.welcomeHeadline}
                  </div>
              {/if}
              {#if !llm || !llm.name || available_llms.length === 0}
                  <div class="cloud-warning-alert">
                      <Alert tone="neutral">
                          <span style="color:#000">{uiTexts.warning.noModelSelected}</span>
                      </Alert>
                  </div>
              {/if}
              {#if chatPrivacyAlertKind && uiTexts}
                  <div class="cloud-warning-alert">
                      <Alert tone={chatPrivacyAlertTone}>
                          <span
                              class={chatPrivacyAlertKind === 'websearch'
                                  ? 'alert-attention-label'
                                  : 'red'}>{uiTexts.warning.attention}</span>
                          <span style="color:#000">{chatPrivacyBody}</span>
                      </Alert>
                  </div>
              {/if}

              {#if documents.length > 0}
                  <DocumentBar disableInteractions={isStreamingLocked}/>
              {/if}

              <div class="input-container-with-buttons">
                  <InputTextarea
                          bind:newMessage
                          bind:onlineSearchEnabled={useWebsearch}
                          {handleEnterKey}
                          bind:reset={resetTextArea}
                          name="chat-eingabe"
                          placeholder={uiTexts.chat.inputPlaceholder}
                          uploadLocked={isStreamingLocked}
                          maxChars={CHAT_CHAR_LIMIT}
                          showOnlineSearch={features?.tools?.onlineSearch}
                          inputPrivacyTone={chatInputPrivacyTone}
                  >
                      <div slot="inside-input" class="send-button-container">
                          <ModelSelection
                                  bind:relevantStore={uiStore.active_llms_chat}
                                  bind:availableLLMs={available_llms}
                                  bind:llmIsSelected={llm_selected_by_user}
                                  bind:selectedLLM={llm}
                          />
                          <button
                                  class="send-button"
                                  aria-label={uiTexts.chat.sendAriaLabel}
                                  disabled={newMessage.length === 0 || isStreamingLocked}
                                  on:click={() => {
                                      if (!isStreamingLocked) {
                                          sendMessage();
                                      }
                                  }}
                                  title={sendButtonTooltip}
                          >
                          </button>
                      </div>
                  </InputTextarea>
              </div>

              <div class="halluzination-warning inline-warning warning-centered">
                  {@html warningChat}
              </div>
          </div>
      </div>

      {#if isInitialState && examplePrompts.length > 0}
          <div class="example-prompts-wrapper">
              <ExamplePrompts
                      prompts={examplePrompts}
                      callback={(prompt) => useExamplePrompt(prompt)}
              />
          </div>
      {/if}
    </div>
</main>

<style>
    * {
        box-sizing: border-box;
    }

    .chat-main {
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        background-color: white;
        text-align: left;
    }

    .chat-page-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        padding-top: 140px;
        padding-bottom: 250px;
        min-height: 100vh;
    }

    .chat-header {
        /* kept for potential future use; currently not visible */
        display: none;
    }

    .reset-button-fixed {
        position: fixed;
        top: 81px;
        right: 150px;
        left: auto;
        transform: none;
        width: auto;
        max-width: none;
        padding: 0;
        z-index: 20;
        display: flex;
        justify-content: flex-end;
        pointer-events: none;
    }

    .reset-button-fixed .secondary {
        pointer-events: auto;
    }

    .chat-intro-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
        padding: 40px 20px;
    }

    .chat-intro-content {
        max-width: 800px;
        width: 100%;
        text-align: center;
    }

    .chat-welcome-headline {
        font-size: clamp(1.3rem, 2vw, 1.6rem);
        font-weight: 500;
        margin: 0 0 0.75rem;
        color: #111827;
        text-align: center;
    }

    .example-prompts-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
        padding: 20px;
    }

    .chat-content-wrapper {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
    }

    .chat-content-wrapper > div {
        position: relative;
        width: 100%;
        max-width: 800px;
        display: flex;
        margin-bottom: 30px;
        align-items: flex-start;
        padding: 0;
    }

    .chat-content-wrapper > div .chat-icon-user,
    .chat-content-wrapper > div .chat-icon-assistant {
        position: absolute;
        left: -52px;
        top: 4px;
        width: 32px;
        height: 32px;
        border-radius: 16px;
        background-repeat: no-repeat;
        background-position: center center;
        background-size: 90%;
        margin-right: 0;
        flex-shrink: 0;
    }

    .chat-content-wrapper > div .chat-icon-assistant {
        background-image: url("static/icon-assistant.svg");
    }

    .chat-content-wrapper > div .chat-icon-user {
        background-image: url("static/icon-user.svg");
    }

    .chat-content-wrapper > div .chat-role {
        font-weight: bold;
    }

    .chat-content-wrapper > div .chat-content {
        width: 100%;
        flex: 1;
        text-align: left;
    }

    .chat-content-wrapper > div .chat-text {
        text-align: left;
    }

    .chat-input-container {
        position: fixed;
        bottom: 30px;
        left: 0;
        right: 0;
        background-color: white;
        padding: 20px;
        display: flex;
        justify-content: center;
        z-index: 0;
    }

    .chat-input-wrapper {
        width: 100%;
        max-width: 800px;
        display: flex;
        flex-direction: column;
    }

    .chat-page-container.initial-state {
        align-items: center;
        justify-content: center;
    }

    .chat-input-container.initial-state-input {
        position: static;
        margin-top: 32px;
        padding-bottom: 0;
        max-width: 800px;
        width: 100%;
    }

    .cloud-warning-alert {
        margin-bottom: 8px;
        display: flex;
        justify-content: center;
    }

    .cloud-warning-alert :global(.alert) {
        text-align: center;
    }

    .input-container-with-buttons {
        position: relative;
    }

    .halluzination-warning {
        margin-top: 12px;
        font-size: 0.8rem;
        color: #666;
        text-align: left;
    }

    .inline-warning {
        margin-top: 4px;
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .warning-centered {
        justify-content: center;
        text-align: center;
        margin-top: 8px;
    }

    .feedback-button-container {
        min-height: 40px;
        margin-top: 8px;
    }

    .inline-loader {
        display: inline-flex;
        margin-left: 8px;
        transform: scale(0.65);
        transform-origin: left center;
    }

    .inline-loader :global(.loader) {
        align-items: center;
    }

    .reasoning-pipe {
        border-left: 2px solid #d1d5db;
        padding-left: 12px;
        margin-left: 2px;
    }

    .reasoning-md {
        color: #4b5563;
        font-size: 0.9375rem;
        line-height: 1.55;
        word-break: break-word;
    }

    .reasoning-md :global(p) {
        margin: 0.4em 0;
    }

    .reasoning-md :global(p:first-child) {
        margin-top: 0;
    }

    .reasoning-md :global(p:last-child) {
        margin-bottom: 0;
    }

    .reasoning-md :global(ul),
    .reasoning-md :global(ol) {
        margin: 0.4em 0;
        padding-left: 1.2rem;
    }

    .reasoning-md :global(li) {
        margin: 0.2em 0;
    }

    .reasoning-md :global(strong) {
        font-weight: 600;
        color: #374151;
    }

    .reasoning-md :global(code) {
        font-family: ui-monospace, 'Cascadia Code', 'Source Code Pro', monospace;
        font-size: 0.86em;
        background: #f3f4f6;
        padding: 0.12em 0.35em;
        border-radius: 4px;
    }

    .reasoning-md :global(pre) {
        margin: 0.5em 0;
        padding: 0.6rem 0.75rem;
        background: #f9fafb;
        border-radius: 6px;
        overflow-x: auto;
        font-size: 0.86em;
        line-height: 1.45;
    }

    .reasoning-md :global(pre code) {
        background: transparent;
        padding: 0;
        font-size: inherit;
    }

    .reasoning-md :global(a) {
        color: #2563eb;
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    .reasoning-md :global(blockquote) {
        margin: 0.4em 0;
        padding-left: 0.75rem;
        border-left: 2px solid #e5e7eb;
        color: #6b7280;
    }

    .reasoning-md :global(h1),
    .reasoning-md :global(h2),
    .reasoning-md :global(h3),
    .reasoning-md :global(h4),
    .reasoning-md :global(h5),
    .reasoning-md :global(h6) {
        font-size: 1em;
        font-weight: 600;
        color: #374151;
        margin: 0.65em 0 0.35em;
        line-height: 1.35;
    }

    .reasoning-md :global(h1:first-child),
    .reasoning-md :global(h2:first-child),
    .reasoning-md :global(h3:first-child) {
        margin-top: 0;
    }

    .reasoning-md :global(hr) {
        margin: 0.75rem 0;
        border: none;
        border-top: 1px solid #e5e7eb;
    }

    @media screen and (max-width: 900px) {
        .reset-button-fixed {
            top: 100px;
            right: 10px;
            left: auto;
            transform: none;
            max-width: none;
            width: auto;
            padding: 0;
            justify-content: flex-end;
        }
    }

    @media screen and (max-width: 900px) {
        .chat-header {
            top: 100px;
            right: 10px;
        }

        .chat-page-container {
            padding-top: 120px;
        }

        .chat-intro-wrapper,
        .example-prompts-wrapper {
            padding: 20px 10px;
        }

        .chat-content-wrapper > div {
            padding: 0;
        }

        .chat-content-wrapper > div .chat-icon-user,
        .chat-content-wrapper > div .chat-icon-assistant {
            left: -46px;
        }

        .chat-input-container {
            padding: 15px 10px;
        }
    }
    @media screen and (max-width: 640px) {
        .chat-page-container {
            padding-top: 96px;
            padding-bottom: 200px;
        }

        .chat-content-wrapper > div {
            margin-bottom: 20px;
        }

        .chat-content-wrapper > div .chat-icon-user,
        .chat-content-wrapper > div .chat-icon-assistant {
            position: static;
            margin-right: 10px;
        }

        .chat-content-wrapper > div .chat-content {
            padding-left: 0;
        }

        .chat-input-container {
            bottom: 0;
            padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
        }

        .chat-input-wrapper {
            max-width: 100%;
        }

        .inline-warning {
            white-space: normal;
        }

        .halluzination-warning {
            text-align: left;
        }

        .input-container-with-buttons {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
    }
    .slow-response-hint {
      margin-top: 0.75rem;
      font-size: 0.9rem;
      color: #444;
      background-color: #fff8e1;
      border-left: 3px solid #f9a825;
      padding: 0.5rem 0.75rem;
      border-radius: 2px;
    }

    @media screen and (max-width: 1200px) {
        .hinweis {
        padding: 0 12px;
        }
    }
</style>
